// src/views/MapPortal/PrintView/index.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout, Checkbox, Input, Button, Space } from 'antd';
import { PrinterOutlined, SyncOutlined, ArrowLeftOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import MapPortalMap from '../MapPortalMap/MapPortalMap';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import Spinner from '../../../shared/components/Spinner';
import { useMapContext } from '../../../context/MapContext';
import './PrintView.scss';
import PrintLegend from './PrintLegend';
import * as Cesium from 'cesium';
import { useQuery } from '@tanstack/react-query';

const { Sider, Content } = Layout;

const PrintView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setState: setMapContext } = useMapContext();
  const viewerRef = useRef<any>(null);
  const { getMapLayers, getMapById } = MapPortalService();
  const { data: mapResponse, isLoading: mapLoading } = useQuery({
    ...getMapById(id),
    enabled: !!id,
  });
  const { data: layersResponse, isLoading: layersLoading } = useQuery({
    ...getMapLayers(id ? Number(id) : undefined),
    enabled: !!id,
  });

  const [mapData, setMapData] = useState<any>(null);
  const [layers, setLayers] = useState<any[]>([]);
  const [orientation, _] = useState<'portrait' | 'landscape'>('landscape');
  const [sidebarVisible, setSidebarVisible] = useState(true);

  const [config, setConfig] = useState({
    title: true,
    description: true,
    legend: true,
    scale: true,
    popup: true,
    northing: true,
    titleText: '',
    descText: '',
  });

  useEffect(() => {
    const res = mapResponse;
    if (!res || !res.map || !id) return;

    let finalMapData = res.map;

    // PARSE URL OVERRIDES
    const params = new URLSearchParams(location.search);
    const lat = params.get('lat');
    const lng = params.get('lng');
    const zoom = params.get('z');

    if (lat && lng && zoom) {
      finalMapData = {
        ...finalMapData,
        general: {
          ...finalMapData.general,
          defaultMapPosition: {
            coordinates: {
              lat: parseFloat(lat),
              lng: parseFloat(lng),
              zoom: parseFloat(zoom),
              heading: parseFloat(params.get('h') || '0'),
              pitch: parseFloat(params.get('p') || '-1.5'),
              roll: parseFloat(params.get('r') || '0'),
            },
          },
        },
      };
    }
    setMapData(finalMapData);
    setMapContext((prev) => ({ ...prev, id: Number(id) }));

    // Initialize text from HTML tags
    setConfig((prev) => ({
      ...prev,
      titleText: res.map.general.mapTitle?.replace(/<[^>]*>/g, '') || 'Untitled Map',
      descText: res.map.general.mapDescription?.replace(/<[^>]*>/g, '') || '',
    }));
  }, [mapResponse, id, setMapContext]);

  useEffect(() => {
    if (layersResponse) {
      setLayers(layersResponse);
    }
  }, [layersResponse]);

  const handleToggleLayer = (layerName: string) => {
    setLayers((prevItems) => prevItems.map((item) => (item.uniqueLayerName === layerName ? { ...item, displayOnMap: !item.displayOnMap } : item)));
  };

  const printLayers = useMemo(() => {
    return layers.map((layer) => ({
      ...layer,
      // If global popup toggle is OFF, disable for all layers
      // If ON, we keep the layer's original popup status
      popupsEnabled: config.popup && layer.popupsEnabled,
      // Force all popups to the bottom left for the print layout
      defaultPosition: 'bottomLeft',
    }));
  }, [layers, config.popup]);

  const [scaleInfo, setScaleInfo] = useState({ km: '0 km', mi: '0 mi' });

  const isLoadingData = mapLoading || layersLoading;

  const updateScale = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const scene = viewer.scene;
    const camera = viewer.camera;
    const canvas = scene.canvas;

    // We calculate the distance for a 100px wide bar in the center of the screen
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const left = new Cesium.Cartesian2(width / 2 - 50, height / 2);
    const right = new Cesium.Cartesian2(width / 2 + 50, height / 2);

    const leftPos = camera.pickEllipsoid(left);
    const rightPos = camera.pickEllipsoid(right);

    if (leftPos && rightPos) {
      const distanceMeters = Cesium.Cartesian3.distance(leftPos, rightPos);

      // Calculate Kilometers
      const km = distanceMeters / 1000;
      const kmText = km >= 1 ? `${km.toFixed(1)} km` : `${(km * 1000).toFixed(0)} m`;

      // Calculate Miles
      const mi = distanceMeters / 1609.34;
      const miText = mi >= 0.1 ? `${mi.toFixed(1)} mi` : `${(mi * 5280).toFixed(0)} ft`;

      setScaleInfo({ km: kmText, mi: miText });
    }
  };

  useEffect(() => {
    const viewer = viewerRef.current;
    if (viewer) {
      // Listen to camera movements
      viewer.camera.changed.addEventListener(updateScale);
      // Initial call
      updateScale();
    }
    return () => {
      viewer?.camera.changed.removeEventListener(updateScale);
    };
  }, [isLoadingData]);

  const handlePrint = () => {
    window.print();
  };

  // Prevent rendering if still loading to avoid Cesium init errors
  if (isLoadingData) return <Spinner isLoading={true} />;
  if (!mapData) return <div className="print-view__error">Map data could not be loaded. Please check your connection.</div>;

  return (
    <Layout className="print-view-root">
      <Sider width={320} trigger={null} collapsible collapsed={!sidebarVisible} collapsedWidth={0} className="print-sidebar no-print">
        <div className="sidebar-inner">
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => window.close()} className="print-view__back-btn">
            Back to Portal
          </Button>

          <div className="config-group">
            <div className="">
              <Checkbox checked={config.title} onChange={(e) => setConfig({ ...config, title: e.target.checked })}>
                Title
              </Checkbox>
            </div>
            <Input value={config.titleText} onChange={(e) => setConfig({ ...config, titleText: e.target.value })} />
          </div>

          <div className="config-group">
            <div className="">
              <Checkbox checked={config.description} onChange={(e) => setConfig({ ...config, description: e.target.checked })}>
                Description
              </Checkbox>
            </div>
            <Input.TextArea rows={3} value={config.descText} onChange={(e) => setConfig({ ...config, descText: e.target.value })} />
          </div>

          <Space direction="vertical" className="print-view__options">
            <Checkbox checked={config.popup} onChange={(e) => setConfig({ ...config, popup: e.target.checked })}>
              Display Popup
            </Checkbox>
            <Checkbox checked={config.legend} onChange={(e) => setConfig({ ...config, legend: e.target.checked })}>
              Display Legend
            </Checkbox>
            <Checkbox checked={config.scale} onChange={(e) => setConfig({ ...config, scale: e.target.checked })}>
              Display Scale
            </Checkbox>
            <Checkbox checked={config.northing} onChange={(e) => setConfig({ ...config, northing: e.target.checked })}>
              Northing
            </Checkbox>
          </Space>

          <Button block icon={<SyncOutlined />} className="refresh-btn" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </div>

        {/* Custom Drawer Handle Button */}
        <Button
          className="sidebar-toggle-handle"
          icon={sidebarVisible ? <LeftOutlined /> : <RightOutlined />}
          onClick={() => setSidebarVisible(!sidebarVisible)}
        />
      </Sider>

      <Content className="print-stage">
        {/* The "Paper" Container */}
        <div className={`map-paper-frame ${orientation}`}>
          <div className="map-inner-container">
            <MapPortalMap
              containerId="print-canvas"
              layers={printLayers as any}
              viewerRef={viewerRef}
              defaultMapPositions={mapData?.general}
              mapId={id}
              boundingLayers={{}}
              drawingMode="point"
              currentTool=""
              setCurrentTool={() => {}}
              popupsToggle={config.popup}
            />

            {/* BOX 1: TOP LEFT - Logo, Title, Desc */}
            {config.title && (
              <div className="print-header-overlay">
                {mapData?.general?.mapLogo && <img src={mapData.general.mapLogo} alt="Logo" className="overlay-logo" />}
                <div className="overlay-text">
                  <div className="overlay-title">{config.titleText}</div>
                  {config.description && <div className="overlay-desc">{config.descText}</div>}
                </div>
              </div>
            )}

            {/* BOX 2: RIGHT SIDE - Static Legend */}
            {/* {config.legend && <PrintLegend onToggleLayer={handleToggleLayer} layers={layers} />} */}
            {config.legend && (
              <PrintLegend
                layers={printLayers}
                onToggleLayer={handleToggleLayer}
                scaleKm={scaleInfo.km} // Pass dynamic scale
                scaleMi={scaleInfo.mi} // Pass dynamic scale
                isNorthingVisible={config.northing}
                isScaleVisible={config.scale}
              />
            )}
          </div>
        </div>
      </Content>

      <Button
        shape="circle"
        icon={<PrinterOutlined />}
        size="large"
        className="floating-print-trigger no-print"
        onClick={handlePrint}
        title="Print Now"
      />
    </Layout>
  );
};

export default PrintView;
